import { 
  stores
 } from '../../mobx-store';
import ExtendedAxios from '../../helperFunctions/ExtendedAxios';
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import EventEmitter from 'eventemitter3';
import {
  refreshTokenRequest,
} from '../../axios/requests';

const MAXIMUM_RETRIES_COUNT = 3;
const DELAY_MILLISECONDS = 1000;

const apiVersion = 'api/v1';

const setBaseURL = () => {
  // Construct Base URL
  return`${process.env.REACT_APP_SERVER_URL}/${apiVersion}`;
};

// const baseURL = 'http://localhost:8080/api/v1';
const baseURL = setBaseURL();


const wadiDeglaAxios = new ExtendedAxios({
  axiosRequestConfig: {
    baseURL,
    timeout: 100000,
  },
  defaultParams: {
  },
});

export enum AuthStatus {
  AUTHORIZED,
  AUTHORIZING,
  UNAUTHORIZED,
}

let authStatus: AuthStatus = AuthStatus.AUTHORIZED;
export const authEvent = new EventEmitter();

authEvent.on('change', (value) => {
  authStatus = value;
});

interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  remainingRetries?: number;
  isRefreshing?: boolean;
  originalRequest?: ExtendedAxiosRequestConfig;
}

interface ExtendedAxiosError {
  config: ExtendedAxiosRequestConfig;
  code?: string;
  request?: any;
  response?: AxiosResponse;
}

interface ExtendedAxiosResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: any;
  config: ExtendedAxiosRequestConfig;
  request?: any;
}

const requestSuccessInterceptor = (async (value: ExtendedAxiosRequestConfig) => {
  // if token is refreshing await the refresh process before dispathing the request
  if (authStatus === AuthStatus.AUTHORIZING) {
    if (value.isRefreshing) {
      return value;
    }
    try {
      await new Promise((res, rej) => {
        authEvent.on('change', (authValue) => {
          if (authValue === AuthStatus.AUTHORIZED) {
            res();
          } else if (authValue === AuthStatus.UNAUTHORIZED) {
            rej(new Error('UNAUTHORIZED'));
          }
        });
      });
      value.headers.Authorization = `Bearer ${stores.authStore.accessToken}`;
      return value;
    } catch (error) {
      return Promise.reject(error);
    }
  }
  return value;
});
wadiDeglaAxios.instance.interceptors.request.use(requestSuccessInterceptor);

const fixRemainingRetries = (remainingRetries: number | undefined) => {
  if (remainingRetries !== null && remainingRetries !== undefined) {
    return remainingRetries;
  }
  if (process && process.env && process.env.REACT_APP_SERVER_URL) {
  return MAXIMUM_RETRIES_COUNT - 1;
  }
  return 0
};

const retryRequest = async (config: ExtendedAxiosRequestConfig): Promise<ExtendedAxiosResponse<any>> => {
  const result = await new Promise<AxiosResponse<any>>((res) => {
    setTimeout(() => {
      config.remainingRetries = fixRemainingRetries(config.remainingRetries) - 1;
      if (config.isRefreshing) {
        config.headers.Authorization = `Bearer ${stores.authStore.refreshToken}`;
      } else {
        config.headers.Authorization = `Bearer ${stores.authStore.accessToken}`;
      }
      res(wadiDeglaAxios.instance(config));
    }, DELAY_MILLISECONDS);
  });
  return result;
};

// eslint-disable-next-line consistent-return
const processFailedRequest = async (error: ExtendedAxiosError): Promise<any> => {
  const remainingRetries = fixRemainingRetries(error.config.remainingRetries);
  if (!remainingRetries) {
    if (error.config.isRefreshing) {
      authEvent.emit('change', AuthStatus.UNAUTHORIZED);
      await stores.authStore.logout();
    }
    return Promise.reject(error);
  }
  // if response status is UNAUTHORIZED
  if (error.response && error.response.status === 401) {
    // if refresh access token process is in progress
    if (authStatus === AuthStatus.AUTHORIZING) {
      if (error.config.isRefreshing) {
        // if this is the refresh access token request
        // logout and return promise rejection
        authEvent.emit('change', AuthStatus.UNAUTHORIZED);
        await stores.authStore.logout();
        return Promise.reject(error);
      }
      // if this is any other request
      // wait until refresh has ended
      try {
        await new Promise((res, rej) => {
          authEvent.on('change', (authValue) => {
            if (authValue === AuthStatus.AUTHORIZED) {
              res();
            } else if (authValue === AuthStatus.UNAUTHORIZED) {
              rej(new Error('UNAUTHORIZED'));
            }
          });
        });
      } catch (error2) {
        return Promise.reject(error2);
      }
      // then retry request
      try {
        const response = await retryRequest(error.config);
        return Promise.resolve(response);
      } catch (error2) {
        const result = await processFailedRequest(error2);
        return result;
      }
    } else if (authStatus === AuthStatus.AUTHORIZED) {
      // if access token expired or is not working anymore
      // change authstatus to authorizing
      authEvent.emit('change', AuthStatus.AUTHORIZING);
      const config: ExtendedAxiosRequestConfig = {
        baseURL,
        url: refreshTokenRequest,
        method: 'post',
        headers: {
          Authorization: `Bearer ${stores.authStore.refreshToken}`,
          'accept-language': wadiDeglaAxios.instance.defaults.headers['accept-language'],
        },
        isRefreshing: true,
        originalRequest: error.config,
      };
      let response: ExtendedAxiosResponse;
      try {
        response = await wadiDeglaAxios.instance(config);
        await stores.authStore.setAccessToken(response.data.data.accessToken);
        authEvent.emit('change', AuthStatus.AUTHORIZED);
      } catch (error2) {
        authEvent.emit('change', AuthStatus.UNAUTHORIZED);
        const result = await processFailedRequest(error2);
        return result;
      }
      try {
        const originalResponse = await retryRequest(response.config.originalRequest as ExtendedAxiosRequestConfig);
        return Promise.resolve(originalResponse);
      } catch (error2) {
        return await processFailedRequest(error2);
      }
    }
  } else {
    try {
      const response = await retryRequest(error.config);
      return Promise.resolve(response);
    } catch (error2) {
      const result = await processFailedRequest(error2);
      return result;
    }
  }
};

const responseSuccessInterceptor = (value: AxiosResponse<any>) => value;
const responseFailureInterceptor = async (error: ExtendedAxiosError) => {
  const result = await processFailedRequest(error);
  return result;
};
wadiDeglaAxios.instance.interceptors.response.use(responseSuccessInterceptor, responseFailureInterceptor);

export default wadiDeglaAxios;
/* eslint-enable @typescript-eslint/no-explicit-any */
